import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Button, Form, Input, Select, Space, Typography, message } from "antd";
import {
  DownOutlined,
  PlusOutlined,
  ReloadOutlined,
  UpOutlined,
} from "@ant-design/icons";
import debounce from "lodash.debounce";
import dayjs from "dayjs";
import { getHostList } from "../api/host";
import HostEditDrawer, {
  CYCLE_MAP,
  PREPAID_MAP,
  ROOM_TYPE_MAP,
  STATUS_MAP,
} from "../components/host/HostEditDrawer";

import UserSelect from "../components/user/UserSelect";

const { Text } = Typography;

const PAGE_SIZE = 20;
// 固定排序：合同到期日升序（最紧急的在前）
const BASE_H = 100; // 顶部栏折叠态高度（含 1px 下边框）：8 + 40 + 9 + 36 + 6 + 1，改了行高要同步改这里

const SORT_OPTIONS = [
  { value: "roomNumber", label: "按房号" },
  { value: "contractExpiryDate", label: "按合同到期" },
  { value: "rentValidDate", label: "按租金到期" },
  { value: "propertyFeeExpiryDate", label: "按物业费到期" },
  { value: "garbageFeeExpiryDate", label: "按垃圾费到期" },
];

const normalize = (all = {}) => ({
  keyword: all.keyword?.trim() || undefined,
  status: all.status || undefined,
  roomType: all.roomType || undefined,
  valid: all.valid || undefined,
  manager: all.manager || undefined,
  sortField: all.sortField || "roomNumber",
});
const countActive = (v) =>
  ["keyword", "status", "roomType", "valid", "manager"].filter((k) => v[k])
    .length;

const readUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user"));
  } catch {
    return null;
  }
};

/* ---------- 到期预警 ---------- */
const EXPIRY_FIELDS = [
  ["contractExpiryDate", "合同"],
  ["rentValidDate", "租金"],
  ["propertyFeeExpiryDate", "物业费"],
  ["garbageFeeExpiryDate", "垃圾费"],
];
const daysUntil = (v) =>
  dayjs(v).startOf("day").diff(dayjs().startOf("day"), "day");
const getAlerts = (r) =>
  EXPIRY_FIELDS.filter(([k]) => r[k])
    .map(([k, label]) => ({ label, days: daysUntil(r[k]) }))
    .filter((a) => a.days <= 30)
    .sort((a, b) => a.days - b.days);
const alertText = (d) =>
  d < 0 ? `已过期${-d}天` : d === 0 ? "今天到期" : `${d}天后到期`;

const userLabel = (v, me) => {
  if (!v) return "—";
  if (typeof v === "object") return v.name || v.username || "—";
  return me && v === me._id ? me.name || me.username || "—" : "—";
};
const money = (n) => (n || n === 0 ? Number(n).toLocaleString() : "—");

/* ---------- 小组件（模块级，避免每次渲染重建） ---------- */
const Pill = ({ cfg }) =>
  cfg ? (
    <span
      style={{
        padding: "2px 8px",
        borderRadius: 20,
        fontSize: 12,
        fontWeight: 600,
        color: cfg.color,
        background: cfg.bg,
        whiteSpace: "nowrap",
      }}
    >
      {cfg.label}
    </span>
  ) : null;

const Chip = ({ color, bg, children }) => (
  <span
    style={{
      flexShrink: 0,
      padding: "2px 7px",
      borderRadius: 5,
      fontSize: 11,
      lineHeight: "18px",
      whiteSpace: "nowrap",
      color,
      background: bg,
    }}
  >
    {children}
  </span>
);

const INVALID = { label: "无效", color: "#8c8c8c", bg: "#f0f0f0" };
const MUNICIPAL_FIELDS = [
  ["propertyFeeExpiryDate", "物业费"],
  ["garbageFeeExpiryDate", "垃圾费"],
];

const HostCard = React.memo(function HostCard({ record: r, me, onClick }) {
  const status = STATUS_MAP[r.status] || {
    label: r.status || "未知",
    color: "#64748b",
    bg: "#f1f5f9",
  };
  const alerts = getAlerts(r);
  const missing =
    r.valid === false ? [] : MUNICIPAL_FIELDS.filter(([k]) => !r[k]);
  const prepaidIdle = r.prepaidWaterElectricityStatus === "waitUse"; // 只提示“未使用”
  const hasFooter =
    alerts.length > 0 ||
    missing.length > 0 ||
    prepaidIdle ||
    r.contractExpiryDate;
  const cycle = CYCLE_MAP[r.rentPaymentCycle]
    ? `${CYCLE_MAP[r.rentPaymentCycle]}${r.rentPaymentCycleMultiplier > 1 ? ` ×${r.rentPaymentCycleMultiplier}` : ""}`
    : null;

  return (
    <div
      onClick={() => onClick(r)}
      style={{
        background: "#fff",
        borderRadius: 12,
        padding: "12px 14px",
        boxShadow: "0 1px 3px rgba(15,23,42,0.06)",
        cursor: "pointer",
        opacity: r.valid === false ? 0.6 : 1,
      }}
    >
      {/* 第一行：房号 楼栋 户型 / 出租情况 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
        }}
      >
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}
        >
          <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
            {r.roomNumber || "—"}
          </span>
          {r.buildingName && (
            <span
              style={{ fontSize: 12, color: "#94a3b8", whiteSpace: "nowrap" }}
            >
              {r.buildingName}
            </span>
          )}
          {r.roomType && (
            <Chip color="#475569" bg="rgba(100,116,139,0.12)">
              {ROOM_TYPE_MAP[r.roomType] || r.roomType}
            </Chip>
          )}
        </div>
        <Space size={4}>
          {r.valid === false && <Pill cfg={INVALID} />}
          <Pill cfg={status} />
        </Space>
      </div>

      {/* 第二行：楼层 楼层级别 面积 / 租金 */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 8,
          marginTop: 8,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 0,
            fontSize: 12,
            color: "#64748b",
          }}
        >
          {r.floorNumber ? <span>{r.floorNumber}F</span> : null}
          {r.floorLevel ? <span>{r.floorLevel}楼层</span> : null}
          {r.area ? <span>{r.area}㎡</span> : null}
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexShrink: 0,
          }}
        >
          <span
            style={{
              padding: "2px 8px",
              borderRadius: 6,
              background: "rgba(82,196,26,0.12)",
              color: "#389e0d",
              fontWeight: 700,
              fontSize: 14,
            }}
          >
            ¥{money(r.rent)}
          </span>
          {cycle && (
            <span style={{ fontSize: 11, color: "#94a3b8" }}>{cycle}</span>
          )}
        </div>
      </div>

      {/* 第三行：人员（不变） */}
      <div
        style={{
          display: "flex",
          gap: 14,
          marginTop: 8,
          fontSize: 12,
          color: "#64748b",
        }}
      >
        <span>管理 {userLabel(r.manager, me)}</span>
        <span>房东 {userLabel(r.owner, me)}</span>
      </div>

      {hasFooter && (
        <div
          style={{
            borderTop: "1px solid #f1f5f9",
            paddingTop: 9,
            marginTop: 10,
            display: "flex",
            flexWrap: "wrap",
            gap: 6,
            alignItems: "center",
          }}
        >
          {/* 物业费/垃圾费没填：实心红色，重点提示 */}
          {missing.map(([k, label]) => (
            <Chip key={k} color="#fff" bg="#ff4d4f">
              {label}未填写
            </Chip>
          ))}
          {alerts.map((a) => {
            const expired = a.days < 0;
            return (
              <Chip
                key={a.label}
                color={expired ? "#cf1322" : "#d46b08"}
                bg={expired ? "rgba(245,34,45,0.1)" : "rgba(250,140,22,0.14)"}
              >
                {a.label} {alertText(a.days)}
              </Chip>
            );
          })}
          {prepaidIdle && (
            <Chip color={PREPAID_MAP.waitUse.color} bg={PREPAID_MAP.waitUse.bg}>
              水电{PREPAID_MAP.waitUse.label}
            </Chip>
          )}
          {alerts.length === 0 && r.contractExpiryDate && (
            <span style={{ fontSize: 12, color: "#94a3b8" }}>
              合同到期 {dayjs(r.contractExpiryDate).format("YYYY-MM-DD")}
            </span>
          )}
        </div>
      )}
    </div>
  );
});

/* ---------- 筛选表单（memo：列表刷新时不重渲染，避免输入框丢焦点） ---------- */
const HEADER_CSS = `
  .host-fr{display:flex;align-items:center;height:34px;padding:0 10px;border-radius:8px;background:rgba(241,245,249,.9);min-width:0}
  .host-fr .ant-input-affix-wrapper,.host-fr .ant-select-selector{background:transparent!important;border:none!important;box-shadow:none!important;padding-inline:0!important}
  .host-fr .ant-input-affix-wrapper{flex:1;padding:0}
  .host-fr .ant-select{flex:1;min-width:0}
`;

const Collapse = ({ open, children }) => (
  <div
    style={{
      display: "grid",
      gridTemplateRows: open ? "1fr" : "0fr",
      transition: "grid-template-rows .2s ease",
    }}
  >
    <div style={{ overflow: "hidden", minHeight: 0 }}>{children}</div>
  </div>
);

const HostHeader = ({
  form,
  defaults,
  total,
  activeCount,
  open,
  hidden,
  scrolled,
  onToggle,
  onAdd,
  onKeyword,
  onCommit,
  onReset,
}) => (
  <div
    style={{
      position: "sticky",
      top: 0,
      zIndex: 20,
      height: BASE_H,
      pointerEvents: "none",
    }}
  >
    <div
      style={{
        pointerEvents: hidden ? "none" : "auto",
        transform: hidden ? "translateY(-120%)" : "translateY(0)",
        transition: "transform .22s ease, box-shadow .2s ease",
        background: "rgba(255,255,255,0.72)",
        backdropFilter: "blur(14px) saturate(160%)",
        WebkitBackdropFilter: "blur(14px) saturate(160%)",
        borderBottom: `1px solid ${scrolled || open ? "rgba(15,23,42,0.06)" : "rgba(15,23,42,0.1)"}`,
        boxShadow: scrolled || open ? "0 3px 12px rgba(15,23,42,0.08)" : "none",
      }}
    >
      <Form
        form={form}
        component={false}
        initialValues={defaults}
        onValuesChange={(changed) =>
          "keyword" in changed ? onKeyword() : onCommit()
        }
      >
        <div
          style={{ maxWidth: 1200, margin: "0 auto", padding: "8px 12px 6px" }}
        >
          {/* 第1行：标题 + 条数 / 关键字 / 新增 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              height: 40,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "baseline",
                gap: 6,
                flexShrink: 0,
              }}
            >
              <span style={{ fontSize: 17, fontWeight: 700, color: "#0f172a" }}>
                房源
              </span>
              <span style={{ fontSize: 12, color: "#94a3b8" }}>{total}</span>
            </div>
            <div className="host-fr" style={{ flex: 1 }}>
              <Form.Item name="keyword" noStyle>
                <Input
                  allowClear
                  variant="borderless"
                  placeholder="输入房号筛选"
                  inputMode="numeric"
                  onPressEnter={onCommit}
                  onBlur={onCommit}
                />
              </Form.Item>
            </div>
            <Button
              type="primary"
              shape="circle"
              icon={<PlusOutlined />}
              aria-label="新增房源"
              onClick={onAdd}
            />
          </div>

          {/* 第2行：状态 / 户型 */}
          <Collapse open={open}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                paddingTop: 6,
              }}
            >
              <div className="host-fr">
                <Form.Item name="status" noStyle>
                  <Select
                    allowClear
                    placeholder="筛选状态"
                    options={Object.entries(STATUS_MAP).map(([value, v]) => ({
                      value,
                      label: v.label,
                    }))}
                  />
                </Form.Item>
              </div>
              <div className="host-fr">
                <Form.Item name="roomType" noStyle>
                  <Select
                    allowClear
                    placeholder="筛选户型"
                    options={Object.entries(ROOM_TYPE_MAP).map(
                      ([value, label]) => ({ value, label }),
                    )}
                  />
                </Form.Item>
              </div>
            </div>
          </Collapse>

          {/* 分割线：折叠时在第1、4行之间，展开时在第2、3行之间 */}
          <div
            style={{
              height: 1,
              margin: "4px 12px",
              background: "rgba(15,23,42,0.08)",
            }}
          />

          {/* 第3行：有效性 / 管理人 */}
          <Collapse open={open}>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 8,
                paddingBottom: 6,
              }}
            >
              <div className="host-fr">
                <Form.Item name="valid" noStyle>
                  <Select
                    allowClear
                    placeholder="筛选有效性"
                    options={[
                      { value: "true", label: "有效" },
                      { value: "false", label: "无效" },
                    ]}
                  />
                </Form.Item>
              </div>
              <div className="host-fr">
                <Form.Item name="manager" noStyle>
                  <UserSelect allowCreate={false} placeholder="筛选管理人" />
                </Form.Item>
              </div>
            </div>
          </Collapse>

          {/* 第4行：筛选 / 已筛选项数 / 排序 / 重置 / 折叠 */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: 36,
              gap: 6,
            }}
          >
            <div
              onClick={onToggle}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                flex: 1,
                minWidth: 0,
                cursor: "pointer",
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 600, color: "#334155" }}>
                筛选
              </span>
              {activeCount > 0 && (
                <span
                  style={{
                    background: "#e6f4ff",
                    color: "#1677ff",
                    borderRadius: 10,
                    padding: "0 7px",
                    fontSize: 12,
                    whiteSpace: "nowrap",
                  }}
                >
                  已筛选 {activeCount} 项
                </span>
              )}
            </div>
            <Form.Item name="sortField" noStyle>
              <Select
                size="small"
                variant="filled"
                style={{ width: 112 }}
                popupMatchSelectWidth={false}
                options={SORT_OPTIONS}
              />
            </Form.Item>
            <Button
              type="text"
              size="small"
              icon={<ReloadOutlined />}
              onClick={onReset}
            >
              重置
            </Button>
            <Button
              type="text"
              size="small"
              aria-label={open ? "收起筛选" : "展开筛选"}
              icon={open ? <UpOutlined /> : <DownOutlined />}
              onClick={onToggle}
            />
          </div>
        </div>
      </Form>
    </div>
  </div>
);

/* ---------- 页面 ---------- */
export default function HostList() {
  const me = useMemo(readUser, []);
  const defaults = useMemo(
    () => ({
      keyword: "",
      status: undefined,
      roomType: undefined,
      valid: "true",
      manager: me?._id,
      sortField: "roomNumber",
    }),
    [me],
  );

  const [form] = Form.useForm();
  const rootRef = useRef(null);
  const scrollerRef = useRef(null);
  const lastY = useRef(0);
  const valuesRef = useRef(normalize(defaults));
  const pageRef = useRef(1);
  const reqId = useRef(0);

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [activeCount, setActiveCount] = useState(() =>
    countActive(normalize(defaults)),
  );
  const [filterOpen, setFilterOpen] = useState(false);
  const [hidden, setHidden] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [drawer, setDrawer] = useState({ open: false, record: null });

  const fetchRows = useCallback(async (nextPage, values) => {
    const id = ++reqId.current; // 防止慢请求覆盖新结果
    setLoading(true);
    try {
      const { sortField, ...filters } = values;
      const { data } = await getHostList({
        ...filters,
        sortField,
        sortOrder: "ascend",
        page: nextPage,
        pageSize: PAGE_SIZE,
      });
      if (id !== reqId.current) return;
      setRows(data?.list || []);
      setTotal(data?.total || 0);
      pageRef.current = nextPage;
      setPage(nextPage);
      setActiveCount(countActive(values));
    } catch (err) {
      if (id === reqId.current)
        message.error(
          err?.response?.data?.message || err?.message || "加载失败",
        );
    } finally {
      if (id === reqId.current) setLoading(false);
    }
  }, []);

  const commit = useCallback(() => {
    valuesRef.current = normalize(form.getFieldsValue());
    fetchRows(1, valuesRef.current);
  }, [form, fetchRows]);

  const debouncedCommit = useMemo(() => debounce(commit, 500), [commit]);
  useEffect(() => () => debouncedCommit.cancel(), [debouncedCommit]);

  const commitNow = useCallback(() => {
    debouncedCommit.cancel();
    commit();
  }, [debouncedCommit, commit]);

  const handleReset = () => {
    debouncedCommit.cancel();
    form.setFieldsValue({ ...defaults });
    commit();
  };

  useEffect(() => {
    fetchRows(1, valuesRef.current);
  }, [fetchRows]);

  /* 滚动：向上滚（看更多）隐藏顶部并收起筛选，向下滚显示。
     用 capture 监听，不管滚动的是 window 还是外层容器都能收到 */
  useEffect(() => {
    const onScroll = (e) => {
      const t = e.target;
      const el = t === document ? document.scrollingElement : t;
      if (!el || (t !== document && !t.contains(rootRef.current))) return; // 忽略下拉框等其它滚动
      scrollerRef.current = el;
      const y = Math.max(0, el.scrollTop);
      if (y > el.scrollHeight - el.clientHeight) return; // iOS 底部回弹

      setScrolled(y > 8);
      // 还没滚过顶部栏本身的高度：它是页面的一部分，不隐藏，否则会露出空白
      if (y <= BASE_H) {
        lastY.current = y;
        setHidden(false);
        return;
      }
      const delta = y - lastY.current;
      if (Math.abs(delta) < 6) return;
      lastY.current = y;
      if (delta > 0) {
        setHidden(true);
        setFilterOpen(false);
      } else {
        setHidden(false);
      }
    };
    window.addEventListener("scroll", onScroll, true);
    return () => window.removeEventListener("scroll", onScroll, true);
  }, []);

  // 手指滑动时让输入框失焦（收键盘），下拉框里滑动除外
  const handleTouchMove = (e) => {
    if (e.target.closest?.(".ant-select-dropdown")) return;
    const a = document.activeElement;
    if (a instanceof HTMLInputElement) a.blur();
  };

  const goPage = (p) => {
    fetchRows(p, valuesRef.current);
    (scrollerRef.current || window).scrollTo({ top: 0 });
  };

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const openDrawer = useCallback(
    (record) => setDrawer({ open: true, record }),
    [],
  );
  const closeDrawer = () => setDrawer((d) => ({ ...d, open: false }));
  const handleSuccess = () => {
    closeDrawer();
    fetchRows(pageRef.current, valuesRef.current);
  };

  return (
    <div
      ref={rootRef}
      onTouchMove={handleTouchMove}
      style={{ background: "#f5f7fa", minHeight: "100dvh" }}
    >
      <style>{HEADER_CSS}</style>

      <HostHeader
        form={form}
        defaults={defaults}
        total={total}
        activeCount={activeCount}
        open={filterOpen}
        hidden={hidden}
        scrolled={scrolled}
        onToggle={() => setFilterOpen((o) => !o)}
        onAdd={() => openDrawer(null)}
        onKeyword={debouncedCommit}
        onCommit={commitNow}
        onReset={handleReset}
      />

      <div
        style={{
          maxWidth: 1200,
          margin: "0 auto",
          padding: "12px 12px calc(16px + env(safe-area-inset-bottom))",
        }}
      >
        <div style={{ opacity: loading ? 0.6 : 1, transition: "opacity .15s" }}>
          {rows.length === 0 && !loading ? (
            <div
              style={{
                textAlign: "center",
                color: "#94a3b8",
                padding: "48px 0",
              }}
            >
              暂无房源，试试调整筛选条件
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
                gap: 10,
              }}
            >
              {rows.map((r) => (
                <HostCard key={r._id} record={r} me={me} onClick={openDrawer} />
              ))}
            </div>
          )}
        </div>

        {total > PAGE_SIZE && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: 16,
              marginTop: 16,
            }}
          >
            <Button
              disabled={page <= 1 || loading}
              onClick={() => goPage(page - 1)}
            >
              上一页
            </Button>
            <Text type="secondary">
              {page} / {totalPages}
            </Text>
            <Button
              disabled={page >= totalPages || loading}
              onClick={() => goPage(page + 1)}
            >
              下一页
            </Button>
          </div>
        )}
      </div>

      <HostEditDrawer
        open={drawer.open}
        record={drawer.record}
        currentUser={me}
        onClose={closeDrawer}
        onSuccess={handleSuccess}
      />
    </div>
  );
}
